import { Box, Grid, IconButton, Stack, Tooltip } from "@mui/material";
import { uploadData } from "aws-amplify/storage";
import { format } from "date-fns";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import AbsForm from "../../abstract/Form";
import CardWithHeader from "../../components/cards/CardWithHeader";
import FileUploadButton from "../../components/inputs/FileUploadButton";
import { useApi } from "../../contexts/ApiContext";
import { useApp } from "../../contexts/AppContext";
import { useAuth } from "../../contexts/AuthContext";
import { useError } from "../../contexts/ErrorContext";
import { FormProvider } from "../../contexts/FormContext";
import { colors } from "../../theme";
import { config } from "./config";
import { createSchema, FormData, FormResponse, SpermFile } from "./types";
import { Pencil, Trash } from "lucide-react";
import { formatDate, toYYYYMM } from "../../utils";

const maximumUploads = 4;

interface SpermAnalysisFormProps {}

type NullableFormData = {
  [K in keyof FormData]?: FormData[K] | null;
};

const SpermAnalysisForm: React.FC<SpermAnalysisFormProps> = (props) => {
  const [uploads, setUploads] = useState<SpermFile[]>([]);
  const { translate } = useApp();
  const translations = useMemo(() => translate("sperm"), [translate]);

  const [replacingIndex, setReplacingIndex] = useState<number | null>(null);
  const [filesMap, setFilesMap] = useState<Map<string, File>>(new Map());
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [anchorIndex, setAnanchorIndex] = useState<number | null>(null);
  const [uploadsErrors, setUploadsErrors] = useState<string[] | null>(null);
  const [mainUploadsError, setMainUploadsError] = useState<string | null>(null);
  const [formData, setFormData] = useState<NullableFormData>({
    patient: "",
    birth: null,
    ethnicity: [],
    height: null,
    weight: null,
    history: "",
    sample: "",
    tech: "",
    files: 0,
  });

  const { updateBreadcrumbs } = useApp();
  const { showError } = useError();
  const navigate = useNavigate();
  const { user, session } = useAuth();
  const { apiService, setSubmitting } = useApi();
  const fileUploadButtonRef = useRef<HTMLButtonElement>(null);
  const [validateOnChange, setValidateOnChange] = useState(false);

  console.info("user", user);

  useEffect(() => {
    updateBreadcrumbs([
      {
        label: translations.breadcrumbs.spemAnalysis,
        onClick: () => {
          navigate("/sperm");
        },
      },
      {
        label: translations.breadcrumbs.newCycle,
      },
    ]);

    sendPreflightRequest();
  }, []);

  const sendPreflightRequest = () => {
    apiService.head("/sperm").then((response) => {
      console.info("preflight response", response);
    });
  };

  const handleUploadReplace = (index: number) => {
    setReplacingIndex(index);
    fileUploadButtonRef.current?.click();
  };

  const handleUploadDelete = (index: number) => {};

  const renderUploads = useCallback(() => {
    if (uploads.length === 0) {
      return <></>;
    }
    return (
      <Grid container spacing={2}>
        {uploads.map((upload, index) => (
          <Grid item xs={12} sm={12} key={index}>
            <CardWithHeader
              headerColor="white"
              title={`Sperm Analysis ${index + 1}`}
            >
              <Stack width="100%" spacing={1} direction={"column"}>
                <Box position="absolute" top={12} right={12}>
                  <Stack direction="row" spacing={1}>
                    <Tooltip title={translations.form.upload.replace}>
                      <IconButton onClick={() => handleUploadReplace(index)}>
                        <Pencil width={24} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={translations.form.upload.delete}>
                      <IconButton onClick={() => handleUploadDelete(index)}>
                        <Trash width={24} />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </Box>
                <Box>
                  <Box position="relative">
                    <video
                      controls
                      playsInline
                      width="100%"
                      src={upload.signedUrl || upload.path}
                    />
                    <Stack direction="column" spacing={1}>
                      {uploadsErrors && uploadsErrors[index] && (
                        <Box color={colors.red[600]}>
                          {uploadsErrors[index]}
                        </Box>
                      )}
                    </Stack>
                  </Box>
                </Box>
              </Stack>
            </CardWithHeader>
          </Grid>
        ))}
      </Grid>
    );
  }, [uploads]);

  const getFormData = useCallback(
    (formData: NullableFormData): FormData => ({
      ...formData,
      patient: formData?.patient ?? "",
      birth: formData.birth ?? null,
      ethnicity: formData.ethnicity ? formData?.ethnicity.map(Number) : [],
      history: formData?.history ?? "",
      sample: formData?.sample ?? "",
      tech: formData?.tech ?? "",
      files: uploads.length,
      height: formData?.height || 0,
      weight: formData?.weight || 0,
    }),
    [uploads]
  );

  const getImagePath = (
    userId: string,
    cycle: string,
    filename: string,
    index: number
  ): string => {
    const ext = filename.split(".").pop();
    return `private/${userId}/sperm/${cycle}/${index}.${ext}`;
  };

  const updateUpload = useCallback(
    (files: File[]) => {
      if (!files) {
        return false;
      }
      if (uploads.length + files.length > maximumUploads) {
        showError(
          translate("sperm", "form.errors.maxImagesExceeded", {
            maximum: `${maximumUploads}`,
          })
        );
        return false;
      }
      
      const uploadsCopy = [...uploads];
      const filesMapCopy = new Map(filesMap);

      if (replacingIndex !== null) {
        const uploadItem = uploads[replacingIndex];
        const file = files[0];
        const url = URL.createObjectURL(file);
        filesMapCopy.set(url, file);
        uploadsCopy[replacingIndex] = {
          ...uploadItem,
          fileUrl: url,
          originalName: file.name,
        } as SpermFile;
        setUploads(uploadsCopy);
        setFilesMap(filesMapCopy);
        setReplacingIndex(null);
        if (fileUploadButtonRef.current?.value) {
          fileUploadButtonRef.current.value = "";
        }
        return;
      }

      for (const file of files) {
        const fileType = file.type.split("/")[0];
        const url = URL.createObjectURL(file);

        let uploadData: SpermFile = {
          path: url,
          label: "",
        };

        uploadData = {
          ...uploadData,
          kind: "video",
          selectedTime: null,
          currentTime: 0,
          thumb: null,
        } as SpermFile;
        uploadsCopy.push(uploadData! as SpermFile);
        filesMapCopy.set(url, file);
        console.info("filesMap", filesMap);
      }
      setFilesMap(filesMapCopy);
      setUploads(uploadsCopy);
      if (fileUploadButtonRef.current?.value) {
        fileUploadButtonRef.current.value = "";
      }
    },
    [uploads, filesMap, user, replacingIndex, formData]
  );

  const upload = async (cycle: string) => {
  console.info("identityId", session?.identityId);

  for (let index = 0; index < uploads.length; index++) {
    try {
      const upload = uploads[index];
      const file = filesMap.get(upload.path!);

      if (!file) {
        console.warn(`No file found for upload index ${index}`);
        continue;
      }

      // Always save with incremental name: 1.mp4, 2.mp4, etc.
      const ext = file.name.split(".").pop() || "mp4";
      const fileName = `${index + 1}.${ext}`;
      const imagePath = `private/${user.clinic}/sperm/${cycle}/${fileName}`;

      await uploadData({
        data: file,
        path: imagePath,
        options: {
          metadata: { label: "" },
        },
      });

      console.info("Uploaded to", imagePath);

      setUploads((prev) => {
        const newUploads = [...prev];
        newUploads[index].path = imagePath;
        return newUploads;
      });
    } catch (error) {
      console.error("Error uploading sperm video", error);
      showError(translations.form.messages.errorUploadingImages);
    }
  }
};


  useEffect(() => {
    if (validateOnChange) {
      validateUploads();
    }
  }, [uploads]);

  const validateUploads = useCallback((): boolean => {
    if (uploads.length === 0) {
      setMainUploadsError(translations.form.messages.imagesRequired);
      return false;
    }
    setMainUploadsError(null);
    return true;
  }, [uploads]);

  const handleSubmit = useCallback(async () => {
    setValidateOnChange(true);
    if (!validateUploads()) {
      return false;
    }

    console.info("Submitting form", formData);

    try {
      setSubmitting(true);
      const response = await apiService.put<FormResponse, FormData>("/sperm", {
        ...getFormData(formData),
        birth: toYYYYMM(formData.birth as Date),
      });
      console.log("API Response:", response);

      await upload(response.cycle.id);

      navigate(`/sperm`, { state: { newId: response.cycle.id } });
    } catch (error) {
      console.error("Error submitting form", error);
      showError(translations.form.messages.errorSubmittingForm);
    } finally {
      setSubmitting(false);
    }
  }, [uploads, filesMap, formData]);

  const handleDataChange = (data: any) => {
    console.info("data", data);
    setFormData(data);
  };

  return (
    <FormProvider>
      <Stack gap={3} alignItems={"flex-start"} direction={"row"}>
        <Box width={"60%"}>
          <AbsForm
            langCategory="sperm"
            data={formData}
            system={user.config.weightHeightUnit ?? "metric"}
            initialValues={{}}
            config={config}
            onSubmit={handleSubmit}
            schema={createSchema(user.config.weightHeightUnit ?? "metric")}
            onChange={handleDataChange}
            submitText={translations.form.form.submit}
            uploads={uploads}
          />
        </Box>
        <Box width={"40%"}>
          <CardWithHeader
            headerColor={"white"}
            title={translations.form.card.title}
          >
            <Stack gap={2}>
              {mainUploadsError && (
                <Box color={colors.red[600]}>{mainUploadsError}</Box>
              )}
              <FileUploadButton
                caption={translations.form.upload.caption}
                title={translations.form.upload.title}
                accept="video/*"
                ref={fileUploadButtonRef as React.RefObject<HTMLButtonElement>}
                keepShowingUploadIcon={true}
                onChange={(files: File[]) => {
                  updateUpload(files);
                }}
                onError={(file, error) => {}}
              />
            </Stack>
          </CardWithHeader>
          {renderUploads()}
        </Box>
      </Stack>
    </FormProvider>
  );
};

export default SpermAnalysisForm;
